import React, {Fragment, useEffect, useState, useCallback, useRef} from 'react';
import { Layout, Col, Row, Typography, Divider, Input, Button, message, Alert, Avatar, Empty, Radio } from 'antd';
import { useHistory } from 'react-router-dom';
import { ApartmentOutlined, BookOutlined, UserOutlined, FileTextOutlined } from '@ant-design/icons';

import KnowledgeSection from '../../components/KnowledgeSection';
import ExpertSection from '../../components/ExpertSection';
import CaseSection from '../../components/CaseSection';
import InstitutionSection from '../../components/InstitutionSection';
import Banner from '../../components/Banner';
import UserDropdown from '../../components/UserDropdown';
import SearchBox from '../../components/SearchBox';
import Foot from '../../components/Foot';
import MaintenanceContent from '../../components/MaintenanceContent';
import EquipmentSharingList from '../../components/EquipmentSharingList';
import FloatingFeedback from '../../components/FloatingFeedback';

import s from './index.module.css';

const { Header, Footer, Content } = Layout;

const getNavItems = (selectedModule) => [
    {
        key: 'institution',
        title: selectedModule?.modelTitle1 || '机构',
        icon: <ApartmentOutlined />,
        sectionId: 'institution-section'
    },
    {
        key: 'knowledge',
        title: selectedModule?.modelTitle2 || '知识',
        icon: <BookOutlined />,
        sectionId: 'knowledge-section'
    },
    {
        key: 'expert',
        title: selectedModule?.modelTitle3 || '专家',
        icon: <UserOutlined />,
        sectionId: 'expert-section'
    },
    {
        key: 'case',
        title: '优秀案例',
        icon: <FileTextOutlined />,
        sectionId: 'case-section'
    }
];

function App() {
    const [selectedModule, setSelectedModule] = useState(null);
    const [slideDirection, setSlideDirection] = useState(null);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showNav, setShowNav] = useState(false);
    const [activeNav, setActiveNav] = useState('institution');
    const history = useHistory();

    // 各个section的ref
    const institutionRef = useRef(null);
    const knowledgeRef = useRef(null);
    const expertRef = useRef(null);
    const caseRef = useRef(null);

    // 手动滚动标记（点击导航时，避免滚动监听回写activeNav造成跳闪）
    const isManualScrollingRef = useRef(false);

    const handleSearch = useCallback((searchData) => {
        const { type, keyword } = searchData;

        if (!keyword || !keyword.trim()) {
            message.warning('请输入搜索关键词');
            return;
        }

        // 根据搜索类型跳转到对应列表页
        const routeMap = {
            institution: '/institutions',
            expert: '/experts',
            knowledge: '/knowledge'
        };

        const route = routeMap[type];
        if (route) {
            history.push(`${route}?keyword=${encodeURIComponent(keyword.trim())}`);
        }
    }, [history]);

    const handleModuleChange = (module, direction) => {

        // 初始化时不触发动画
        if (!isInitialized) {
            setIsInitialized(true);
            setSelectedModule(module);
            return;
        }

        if (isAnimating) return;

        setIsAnimating(true);
        setSlideDirection(direction);

        // 延迟更新模块，让动画先开始
        setTimeout(() => {
            setSelectedModule(module);
        }, 50);

        // 动画结束后重置状态
        setTimeout(() => {
            setIsAnimating(false);
            setSlideDirection(null);
        }, 500);
    };

    // 检查当前模块是否为维护中模块
    const isMaintenanceModule = (module) => {
        if (!module) return false;
        // 维护中的模块：标准化服务(5)
        const maintenanceModules = [5];
        return maintenanceModules.includes(module.id);
    };

    // 获取滑动动画类名
    const getSlideClassName = () => {
        if (!isAnimating || !isInitialized) {
            return '';
        }
        const className = slideDirection === 'right' ? s.slideInRight : s.slideInLeft;
        return className;
    };

    // 滚动监听
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            // 检查是否在四大模块区域内
            if (institutionRef.current && caseRef.current) {
                const firstSectionTop = institutionRef.current.offsetTop;
                const lastSectionBottom = caseRef.current.offsetTop + caseRef.current.offsetHeight;
                const headerHeight = 120; // header高度

                // 判断是否在模块区域内（考虑header偏移）
                const isInModuleArea = currentScrollY + headerHeight >= firstSectionTop &&
                                        currentScrollY + headerHeight <= lastSectionBottom;

                // console.log('滚动监听:', {
                //     currentScrollY,
                //     firstSectionTop,
                //     lastSectionBottom,
                //     isInModuleArea,
                //     shouldShow: isInModuleArea && currentScrollY > 100
                // });

            // 只在模块区域内且滚动超过100px时显示导航条
            setShowNav(isInModuleArea && currentScrollY > 100);

            // 手动滚动期间，不根据滚动位置更新激活导航项（避免点击导航时activeNav跳闪）
            if (isManualScrollingRef.current) {
                return;
            }
        }

        // 更新激活的导航项

            const sections = [
                { id: 'institution', ref: institutionRef },
                { id: 'knowledge', ref: knowledgeRef },
                { id: 'expert', ref: expertRef },
                { id: 'case', ref: caseRef }
            ];

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (section.ref && section.ref.current) {
                    const rect = section.ref.current.getBoundingClientRect();
                    if (rect.top <= 200) {
                        setActiveNav(section.id);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // 平滑滚动到指定section
    const scrollToSection = (sectionId) => {
        const refs = {
            'institution-section': institutionRef,
            'knowledge-section': knowledgeRef,
            'expert-section': expertRef,
            'case-section': caseRef
        };

        const ref = refs[sectionId];
        if (ref && ref.current) {
            const offset = 120; // 考虑header的高度
            const elementPosition = ref.current.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            // 手动滚动期间标记，避免滚动监听回写activeNav造成跳闪
            isManualScrollingRef.current = true;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });

            // 更新激活状态
            const navItem = getNavItems(selectedModule).find(item => item.sectionId === sectionId);
            if (navItem) {
                setActiveNav(navItem.key);
            }

            // 平滑滚动动画结束后清除标记
            setTimeout(() => {
                isManualScrollingRef.current = false;
            }, 600);
        }
    };

    // 点击导航项
    const handleNavClick = (navItem) => {
        scrollToSection(navItem.sectionId);
    };


  return (
      <Layout className={s.layoutBox}>
          {/* 固定导航条 */}
          <div className={`${s.fixedNav} ${showNav ? s.visible : ''}`}>
              <div className={s.navContainer}>
                  {getNavItems(selectedModule).map(item => (
                      <div
                          key={item.key}
                          className={`${s.navItem} ${activeNav === item.key ? s.active : ''}`}
                          onClick={() => handleNavClick(item)}
                      >
                          <span className={s.navIcon}>{item.icon}</span>
                          <span className={s.navTitle}>{item.title}</span>
                      </div>
                  ))}
              </div>
          </div>

          <Header className={s.headBox}>
              <div className={s.headerInner}>
                  <div className={s.brand}>
                      <img src="/logo.jpg" alt="logo" className={s.brandLogo}/>
                      <span className={s.brandTitle}>庆阳市质量基础设施云平台</span>
                  </div>

                  <div className={s.headerSearchBox}>
                      <SearchBox onSearch={handleSearch} />
                  </div>
              </div>
              <div className={s.headerActions}>
                  <UserDropdown />
              </div>
          </Header>
          <Content className={s.contentBox}>
              {/* 新的Banner组件 */}
              <Banner onModuleChange={handleModuleChange} />

              {/* 内容区域 - 带滑动动画 */}
              <div className={`${s.contentSections} ${getSlideClassName()}`}>
                  {isInitialized && selectedModule ? (
                      isMaintenanceModule(selectedModule) ? (
                          // 维护中模块显示维护中内容
                          <div className={s.aaa}>
                              <div className={s.maintenanceWrapper}>
                                  <MaintenanceContent moduleName={selectedModule.title} />
                              </div>
                          </div>
                      ) : selectedModule.id === 7 ? (
                          // 仪器设备模块：复用 /equipment-sharing 设备列表（内嵌隐藏 Hero 顶部横幅）
                          <div className={s.aaa}>
                              <EquipmentSharingList embedded showHeader={false} />
                          </div>
                          ) : (
                          // 正常模块显示各个Section
                          <div className={s.aaa}>
                              {/* 检验检测机构模块 */}
                              <div ref={institutionRef} id="institution-section">
                                  <InstitutionSection selectedModule={selectedModule} />
                              </div>

                              {/* 检验检测知识模块 */}
                              <div ref={knowledgeRef} id="knowledge-section">
                                  <KnowledgeSection selectedModule={selectedModule} />
                              </div>

                              {/* 检验检测专家模块 */}
                              <div ref={expertRef} id="expert-section">
                                  <ExpertSection selectedModule={selectedModule} />
                              </div>

                              {/* 优秀案例模块 */}
                              <div ref={caseRef} id="case-section">
                                  <CaseSection selectedModule={selectedModule} />
                              </div>
                          </div>
                      )
                  ) : (
                      // 默认显示检验检测机构模块（初始加载时）
                      <div className={s.bbb}>
                          <div ref={institutionRef} id="institution-section">
                              <InstitutionSection selectedModule={selectedModule} />
                          </div>
                          <div ref={knowledgeRef} id="knowledge-section">
                              <KnowledgeSection selectedModule={selectedModule} />
                          </div>
                          <div ref={expertRef} id="expert-section">
                              <ExpertSection selectedModule={selectedModule} />
                          </div>
                          <div ref={caseRef} id="case-section">
                              <CaseSection selectedModule={selectedModule} />
                          </div>
                      </div>
                  )}
              </div>
          </Content>
          <Footer className={s.footerBox}>
              <Foot />
          </Footer>
          <FloatingFeedback />
      </Layout>
  )

}

export default App;
